import { Button, HStack, Stack, Text } from '@chakra-ui/react';
import { useMutation } from '@tanstack/react-query';
import { type FC } from 'react';
import { useTranslation } from 'react-i18next';

import { FullModal } from '$/components/common/Modals/Modal';
import { useCloudMessageTranslation } from '$/hooks/useCloudMessageTranslation';
import { useToast } from '$/hooks/useToast';
import { deleteInvitation } from '$/services/usecases/invitation';
import { type TeacherInvitationRequest } from '$/services/usecases/invitation/mapper/invitation';

interface Props {
  invitation: TeacherInvitationRequest;
  isOpen: boolean;
  onClose: () => void;
}

export const DeleteTeacherInvitationModal: FC<Props> = ({
  invitation,
  isOpen,
  onClose,
}) => {
  const { t } = useTranslation();
  const toast = useToast();
  const { cloudMessageTranslation } = useCloudMessageTranslation();

  const { mutate: deleteInvite, isPending } = useMutation({
    mutationFn: () => deleteInvitation(invitation.id),
    onSuccess: (data) => {
      if (!data.isSuccessful) {
        toast(cloudMessageTranslation(data.response.message), 'error', '');
        return;
      }
      toast(t('school.teachers.deleteInvitationSuccess'), 'success', '');
      onClose();
    },
  });

  return (
    <FullModal
      isOpen={isOpen}
      onClose={onClose}
      header={t('school.teachers.deleteInvitationModal.title')}
      size='xl'
      isScrollable={false}
    >
      <Stack gap='6'>
        <Text>
          {t('school.teachers.deleteInvitationModal.description', {
            email: invitation.email,
          })}
        </Text>
        <HStack justify='space-between'>
          <Button isDisabled={isPending} onClick={onClose} variant='ghost'>
            {t('general.cancel')}
          </Button>
          <Button
            isLoading={isPending}
            onClick={() => deleteInvite()}
            variant='danger'
          >
            {t('general.delete')}
          </Button>
        </HStack>
      </Stack>
    </FullModal>
  );
};
